import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useAtom, useAtomValue } from "jotai";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RevealOverlay } from "@/components/RevealOverlay";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn, generateTextShadow } from "@/lib/utils";
import { t } from "@/lib/i18n";
import { homeUiReadyAtom } from "@/lib/homeUiAtoms";
import { isGameLiveAtom } from "@/lib/gameStatusAtom";
import { isErrorPageAtom } from "@/lib/atoms";
import { missionWidgetOpenAtom } from "@/lib/missionWidgetAtom";
import {
  useClaimMission,
  useDailyStreakMission,
  useStats,
  useWeeklyStreakMission,
} from "@/lib/hooks/useApi";
import type { Mission } from "@/lib/types/api";
import { usePrivy } from "@privy-io/react-auth";
import { toast } from "sonner";
import { translateStreakMissionDescription } from "@/lib/streakMissionTranslations";
import {
  getMissionRoute,
  shouldOpenUrlFirst,
} from "@/routes/(game)/missions/daily-mission";
import { Countdown } from "@/components/ui/countdown";
import { SheetCloseIcon } from "@/components/ui/sheet";

import { MissionList } from "./MissionList";
import { DailyBonusCard } from "./DailyBonusCard";
import { WeeklyBonusTracker } from "./WeeklyBonusTracker";
import { MonthlyBonusTracker } from "./MonthlyBonusTracker";
import {
  ALLOWED_ROUTES,
  BUTTON_CLIP_PATH,
  BADGE_CLIP_PATH,
  getRewardIcon,
} from "./missionConstants";

export function MissionWidget() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, authenticated } = usePrivy();
  const [isOpen, setIsOpen] = useAtom(missionWidgetOpenAtom);
  const [isClaimInProgress, setIsClaimInProgress] = useState(false);
  const [activeTab, setActiveTab] = useState<"daily" | "weekly">("daily");
  const [pendingAutoClose, setPendingAutoClose] = useState(false);
  const isGameLive = useAtomValue(isGameLiveAtom);
  const isHomeUiReady = useAtomValue(homeUiReadyAtom);
  const isErrorPage = useAtomValue(isErrorPageAtom);

  const walletAddress = user?.wallet?.address ?? "";
  const { data: statsData } = useStats(walletAddress);
  const userId = statsData?.userId;

  // missions-v2: split into two independent fetches.
  const {
    data: dailyData,
    isLoading: isDailyLoading,
    refetch: refetchDaily,
  } = useDailyStreakMission(userId);
  const {
    data: weeklyData,
    isLoading: isWeeklyLoading,
    refetch: refetchWeekly,
  } = useWeeklyStreakMission(userId);
  const { mutateAsync: claimMission } = useClaimMission();

  const isStreakLoading = isDailyLoading || isWeeklyLoading;
  const refetchStreakMission = useCallback(() => {
    void refetchDaily();
    void refetchWeekly();
  }, [refetchDaily, refetchWeekly]);

  const tabData = activeTab === "daily" ? dailyData : weeklyData;

  const [isCompletionBonusPending, setIsCompletionBonusPending] =
    useState(false);
  const [isStreakBonusPending, setIsStreakBonusPending] = useState(false);

  const isBonusClaimPending = isCompletionBonusPending || isStreakBonusPending;

  const dailyMissions = useMemo(() => dailyData?.missions ?? [], [dailyData]);
  const weeklyMissions = useMemo(
    () => weeklyData?.missions ?? [],
    [weeklyData],
  );

  const activeMissions = activeTab === "daily" ? dailyMissions : weeklyMissions;

  const [clickedMissions, setClickedMissions] = useState<
    Record<string, boolean>
  >({});

  const handleMissionClicked = useCallback((missionId: string) => {
    setClickedMissions((prev) => ({ ...prev, [missionId]: true }));
  }, []);

  const eligibleCount = useMemo(
    () =>
      [...dailyMissions, ...weeklyMissions].filter((m) => {
        if (m.isClaimed) return false;
        if (shouldOpenUrlFirst(m.type)) {
          const url = m.details.url?.trim();
          return Boolean(url) && Boolean(clickedMissions[m.id]);
        }
        return m.isEligible;
      }).length,
    [dailyMissions, weeklyMissions, clickedMissions],
  );

  const isAllowedRoute = ALLOWED_ROUTES.some((r) =>
    location.pathname.startsWith(r),
  );

  // Claiming state
  const [activeMissionId, setActiveMissionId] = useState<string | null>(null);
  const [claimedMission, setClaimedMission] = useState<Mission | null>(null);
  const [isRevealOpen, setIsRevealOpen] = useState(false);

  const completionBonusIsClaimed = tabData?.completionBonus.isClaimed ?? false;
  const streakBonusIsClaimed = tabData?.streakBonus.isClaimed ?? false;

  const progressData = useMemo(() => {
    const missions: Mission[] =
      activeTab === "daily" ? dailyMissions : weeklyMissions;
    const isDone = (m: Mission) =>
      shouldOpenUrlFirst(m.type) ? m.isClaimed : m.isClaimed || m.isEligible;
    const completed = missions.filter(isDone).length;
    const total = missions.length;
    const isComplete = total > 0 && missions.every(isDone);

    return {
      isComplete,
      isClaimed: completionBonusIsClaimed,
      missionsCompleted: completed,
      missionsTotal: total,
    };
  }, [activeTab, dailyMissions, weeklyMissions, completionBonusIsClaimed]);

  // Refetch mission data when dialog opens
  useEffect(() => {
    if (isOpen && userId != null) {
      refetchStreakMission();
    }
  }, [isOpen, userId, refetchStreakMission]);

  // Auto-close logic when game goes live
  useEffect(() => {
    if (isGameLive && isOpen) {
      if (isClaimInProgress || isBonusClaimPending) {
        setPendingAutoClose(true);
      } else {
        setIsOpen(false);
      }
    }
  }, [isGameLive, isOpen, isClaimInProgress, isBonusClaimPending]);

  // Execute pending auto-close after claim completes
  useEffect(() => {
    if (pendingAutoClose && !isClaimInProgress && !isBonusClaimPending) {
      setIsOpen(false);
      setPendingAutoClose(false);
    }
  }, [pendingAutoClose, isClaimInProgress, isBonusClaimPending]);


  // Individual mission claim
  const handleClaim = useCallback(
    async (missionId: string) => {
      const allMissions = [...dailyMissions, ...weeklyMissions];
      const mission = allMissions.find((m) => m.id === missionId);
      if (!mission) return;

      setActiveMissionId(missionId);
      setIsClaimInProgress(true);

      try {
        await claimMission({ missionId });

        // Close dialog first so RevealOverlay isn't blocked by focus trap
        setIsOpen(false);
        setClaimedMission(mission);
        setIsRevealOpen(true);
      } catch {
        toast.error("Failed to claim mission. Please try again.");
      } finally {
        setActiveMissionId(null);
        setIsClaimInProgress(false);
      }
    },
    [dailyMissions, weeklyMissions, claimMission],
  );

  const handleStart = useCallback(
    (mission: Mission) => {
      const url = mission.details.url?.trim();
      if (url) {
        if (url.startsWith("/")) {
          navigate({ to: url });
        } else if (typeof window !== "undefined") {
          window.open(url, "_blank", "noopener,noreferrer");
        }
        setIsOpen(false);
        return;
      }

      const route = getMissionRoute(mission.type);
      if (route) {
        navigate({ to: route });
        setIsOpen(false);
      }
    },
    [navigate],
  );

  // Completion bonus claim — needs `missionId` per missions-v2. BE may not
  // expose it on the bonus envelope yet; toast a friendly notice in that case.
  const handleClaimCompletionBonus = useCallback(async () => {
    const missionId = tabData?.completionBonus.missionId;
    if (!missionId) {
      toast.info("Bonus claim coming soon", {
        description: "Backend support for this claim is rolling out.",
      });
      return;
    }
    setIsCompletionBonusPending(true);
    try {
      await claimMission({ missionId });
    } catch {
      toast.error("Failed to claim bonus. Please try again.");
    } finally {
      setIsCompletionBonusPending(false);
    }
  }, [tabData, claimMission]);

  // Streak bonus claim (passed to children) — same missionId-from-envelope
  // pattern as the completion bonus above.
  const handleClaimStreakBonus = useCallback(async () => {
    const missionId = tabData?.streakBonus.missionId;
    if (!missionId) {
      toast.info("Bonus claim coming soon", {
        description: "Backend support for this claim is rolling out.",
      });
      return;
    }
    setIsStreakBonusPending(true);
    try {
      await claimMission({ missionId });
    } catch {
      toast.error("Failed to claim streak bonus. Please try again.");
    } finally {
      setIsStreakBonusPending(false);
    }
  }, [tabData, claimMission]);

  if (
    !authenticated ||
    (!isAllowedRoute && !isOpen && !isRevealOpen) ||
    isGameLive ||
    isErrorPage ||
    (location.pathname === "/world" && !isHomeUiReady)
  ) {
    return null;
  }

  const showWeeklyBonusStar =
    dailyData?.streakBonus.isComplete && !dailyData?.streakBonus.isClaimed;

  return (
    <>
      {!isOpen && (
        <>
          {/* Floating trigger button */}
          <div className="fixed bottom-[calc(41svh+72px)] right-0 z-999">
            {/* Badge count */}
            {eligibleCount > 0 && (
              <span className="absolute top-0 -left-1 flex h-4 w-4 z-20">
                <span
                  className="animate-ping absolute inline-flex h-full w-full bg-red-400 opacity-75"
                  style={{ clipPath: BADGE_CLIP_PATH }}
                />
                <span
                  className="relative inline-flex h-4 w-4 bg-red-500 border border-white/20 items-center justify-center"
                  style={{ clipPath: BADGE_CLIP_PATH }}
                >
                  <span
                    className="text-[14px] font-bold text-white leading-none"
                    style={{ textShadow: generateTextShadow(0.5, "#991b1b") }}
                  >
                    {eligibleCount > 9 ? "9+" : eligibleCount}
                  </span>
                </span>
              </span>
            )}

            {/* Star indicator for streak bonus */}
            {showWeeklyBonusStar && (
              <span className="absolute -top-1 -left-1 z-30">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
              </span>
            )}

            <Button
              onClick={() => setIsOpen(true)}
              className="h-16 w-8 z-100 bg-black rounded-none! transition-all duration-300 hover:scale-110"
              size="icon"
              style={{ clipPath: BUTTON_CLIP_PATH }}
            >
              <svg
                className="h-12! w-auto"
                viewBox="0 0 11 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M8.5 0H2.5V0.5H8.5V0Z" fill="white" />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M0 15V2H2V0.5H2.5V2V3H8.5V2V0.5H9V2H11V15H0ZM10.5 2.5V14.5H10V2.5H10.5ZM0.5 2.5V14.5H1V2.5H0.5ZM1.5 2.5V14.5H9.5V2.5H9V3.5H2V2.5H1.5Z"
                  fill="white"
                />
                <path d="M7.5 1.5H6.5V2H7.5V1.5Z" fill="white" />
                <path d="M3.5 1.5H4.5V2H3.5V1.5Z" fill="white" />
                <path
                  d="M2.85294 7.5V7.83333H2.5V8.83333H2.85294V9.16667H3.20588V9.5H3.55882V9.83333H3.91176V10.1667H4.26471V10.5H5.67647V10.1667H6.02941V9.83333H6.38235V9.5H6.73529V9.16667H7.08824V8.83333H7.44118V8.5H7.79412V8.16667H8.14706V7.83333H8.5V6.83333H8.14706V6.5H7.44118V6.83333H7.08824V7.16667H6.73529V7.5H6.38235V7.83333H6.02941V8.16667H5.67647V8.5H4.61765V8.16667H4.26471V7.83333H3.91176V7.5H2.85294Z"
                  fill="white"
                />
              </svg>
            </Button>
          </div>
        </>
      )}

      {/* Mission dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-100! sm:max-w-105! max-h-[85svh] sm:max-h-[90svh] p-0 gap-2! overflow-hidden flex! flex-col">
          <DialogHeader className="hidden px-4 pt-4 pb-2">
            <DialogTitle
              className="text-white font-black text-xl tracking-wide text-center uppercase flex items-center justify-center gap-1.5"
              style={{ textShadow: generateTextShadow(1) }}
            >
              {t("streakMissions.title")}
            </DialogTitle>
          </DialogHeader>
          <DialogClose>
            <SheetCloseIcon className="absolute top-2 right-2 hover:cursor-pointer hover:saturate-98 hover:brightness-98 active:scale-98" />
          </DialogClose>

          {/* Tab buttons — full-width, side by side */}
          <div
            className="flex gap-2 px-4 mt-6"
            role="tablist"
            aria-label="Mission tabs"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "daily"}
              disabled={isBonusClaimPending}
              onClick={() => setActiveTab("daily")}
              className={cn(
                "hover:cursor-pointer",
                "flex-1 py-2 text-sm font-bold uppercase rounded-lg border-2 transition-colors",
                activeTab === "daily"
                  ? "bg-green-500/20 text-green-300 border-green-400/60"
                  : "bg-gray-700/30 text-gray-400 border-gray-600/40 hover:text-gray-300 hover:border-gray-500/50",
                isBonusClaimPending && "pointer-events-none opacity-50",
              )}
              aria-label="Show daily missions"
            >
              {t("streakMissions.tabs.daily")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "weekly"}
              disabled={isBonusClaimPending}
              onClick={() => setActiveTab("weekly")}
              className={cn(
                "hover:cursor-pointer",
                "flex-1 py-2 text-sm font-bold uppercase rounded-lg border-2 transition-colors",
                activeTab === "weekly"
                  ? "bg-purple-500/20 text-purple-300 border-purple-400/60"
                  : "bg-gray-700/30 text-gray-400 border-gray-600/40 hover:text-gray-300 hover:border-gray-500/50",
                isBonusClaimPending && "pointer-events-none opacity-50",
              )}
              aria-label="Show weekly missions"
            >
              {t("streakMissions.tabs.weekly")}
            </button>
          </div>

          {/* Progress section */}
          <DailyBonusCard
            missionsCompleted={progressData.missionsCompleted}
            missionsTotal={progressData.missionsTotal}
            isComplete={progressData.isComplete}
            isClaimed={progressData.isClaimed}
            isPending={isCompletionBonusPending}
            onClaim={handleClaimCompletionBonus}
            variant={activeTab}
            prize={tabData?.completionBonus.prize ?? null}
            reward={
              tabData?.completionBonus.reward ?? {
                type: "lucky_draw",
                amount: 0,
              }
            }
          />

          {/* Mission list (scrollable) */}
          <div className="min-h-0 overflow-y-auto px-4 py-2 space-y-2">
            <MissionList
              missions={activeMissions}
              isLoading={isStreakLoading}
              activeMissionId={activeMissionId}
              onClaim={handleClaim}
              onStart={handleStart}
              clickedMissions={clickedMissions}
              onMissionClicked={handleMissionClicked}
            />
          </div>

          {/* Streak bonus section */}
          {(dailyData || weeklyData) && (
            <div className="px-4 pt-3 pb-2 flex flex-col gap-2 border-t-4 border-[#0659B5]">
              <div
                role="tabpanel"
                className={cn("rounded-sm p-3")}
                style={{
                  aspectRatio: "32/9",
                  backgroundImage: `linear-gradient(rgba(0,0,0,0.2), rgba(0,0,0,0.2)), url(/assets/${activeTab === "daily" ? "daily-bonus-tracker-bg" : "weekly-bonus-tracker-bg"}.png)`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  imageRendering: "pixelated",
                }}
              >
                {activeTab === "daily" && dailyData && (
                  <WeeklyBonusTracker
                    streakData={{
                      ...dailyData.streakBonus,
                      isClaimed: streakBonusIsClaimed,
                    }}
                    onClaim={handleClaimStreakBonus}
                    isPending={isStreakBonusPending}
                  />
                )}
                {activeTab === "weekly" && weeklyData && (
                  <MonthlyBonusTracker
                    streakData={{
                      ...weeklyData.streakBonus,
                      isClaimed: streakBonusIsClaimed,
                    }}
                    onClaim={handleClaimStreakBonus}
                    isPending={isStreakBonusPending}
                  />
                )}
              </div>
            </div>
          )}

          {/* Footer countdown */}
          <div className="flex items-center justify-between px-4 py-2 border-t-4 border-[#0659B5]">
            <span
              className="text-white/85 text-xs"
              style={{ textShadow: generateTextShadow(1) }}
            >
              {t(`streakMissions.resetLabel.${activeTab}`)}
            </span>
            <div className="flex items-center justify-center gap-2 black-container h-8 px-1 opacity-75">
              <img
                src="https://cdn.navalabs.io/icons/timer_rqnxy6.png"
                className="w-3.5"
                alt={"timer"}
              />
              <Countdown
                startTimestamp={tabData?.resetTimestamp ?? 0}
                className="text-xs"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reveal overlay for claimed missions */}
      <RevealOverlay
        isOpen={isRevealOpen && Boolean(claimedMission)}
        onClose={() => {
          setIsRevealOpen(false);
          setClaimedMission(null);
        }}
        title={t("streakMissions.missionCompleted")}
        rewardText={t("streakMissions.reward")}
        rewardValue={claimedMission?.rewardAmount ?? 0}
        buttonText={t("streakMissions.close")}
        description={
          claimedMission
            ? t("streakMissions.youCompleted", {
                description: translateStreakMissionDescription(
                  claimedMission.description,
                ),
              })
            : undefined
        }
        shouldUseRewardIcons={
          claimedMission?.rewardType !== "free_pass" &&
          claimedMission?.rewardType !== "gacha_coin" &&
          claimedMission?.rewardType !== "lucky_draw"
        }
        alternatePrizeImageSrc={
          claimedMission ? getRewardIcon(claimedMission.rewardType) : undefined
        }
        alternatePrizeImageSrcSize="h-24! w-auto"
        alternatePrizeText={
          claimedMission ? `+${claimedMission.rewardAmount}` : undefined
        }
      />
    </>
  );
}
